import { DisposableGroup } from '@blocksuite/global/disposable';
import {
  Bound,
  deserializeXYWH,
  getBoundWithRotation,
  getPointsFromBoundWithRotation,
  type IVec,
  linePolygonIntersects,
  PointLocation,
  polygonGetPointTangent,
  polygonNearestPoint,
  randomSeed,
  rotatePoints,
  type SerializedXYWH,
  type XYWH,
} from '@blocksuite/global/gfx';
import { createMutex } from 'lib0/mutex';
import isEqual from 'lodash-es/isEqual';
import { Subject } from 'rxjs';
import * as Y from 'yjs';

import {
  descendantElementsImpl,
  hasDescendantElementImpl,
  isLockedByAncestorImpl,
  isLockedBySelfImpl,
  isLockedImpl,
  loadedDescendantElementsImpl,
  lockElementImpl,
  unlockElementImpl,
} from '../../../utils/tree.js';
import type { EditorHost } from '../../../view/index.js';
import type { TextSpan } from '../../text-reveal.js';
import type {
  GfxCompatibleInterface,
  GfxGroupCompatibleInterface,
  PointTestOptions,
} from '../base.js';
import { gfxGroupCompatibleSymbol } from '../base.js';
import { groupMembers, loadedGroupMembers } from '../group-members.js';
import type { GfxGroupModel, GfxModel } from '../model.js';
import {
  convertProps,
  field,
  getDerivedProps,
  getFieldPropsSet,
  local,
  updateDerivedProps,
  watch,
} from './decorators/index.js';
import type { SurfaceBlockModel } from './surface-model.js';

export type BaseElementProps = {
  index: string;
  seed: number;
  lockedBySelf?: boolean;
  comments?: Record<string, boolean>;
};

export type SerializedElement = Record<string, unknown> & {
  type: string;
  xywh: SerializedXYWH;
  id: string;
  index: string;
  lockedBySelf?: boolean;
  comments?: Record<string, boolean>;
  props: Record<string, unknown>;
};
export abstract class GfxPrimitiveElementModel<
  Props extends BaseElementProps = BaseElementProps,
> implements GfxCompatibleInterface {
  private _lastXYWH: SerializedXYWH | null = null;

  private _transientRotate: number | undefined = undefined;

  private _transientTextHidden: TextSpan | undefined = undefined;

  private _transientXYWH: SerializedXYWH | undefined = undefined;

  protected _disposable = new DisposableGroup();

  protected _id: string;

  protected _local = new Map<string | symbol, unknown>();

  protected _onChange: (payload: {
    props: Record<string, unknown>;
    oldValues: Record<string, unknown>;
    local: boolean;
  }) => void;

  /**
   * Used to store a copy of data in the yMap.
   */
  protected _preserved = new Map<string, unknown>();

  protected _stashed: Map<keyof Props | string, unknown>;

  /**
   * The ID of the parent group. Set by the group when this element is added/removed.
   * Enables O(1) parent lookup instead of O(n) iteration through all groups.
   */
  _parentGroupId: string | null = null;

  propsUpdated = new Subject<{ key: string }>();

  abstract rotate: number;

  surface!: SurfaceBlockModel;

  abstract xywh: SerializedXYWH;

  yMap: Y.Map<unknown>;

  get connectable() {
    return true;
  }

  get deserializedXYWH() {
    const xywh = this._transientXYWH ?? this.xywh;
    if (!this._lastXYWH || xywh !== this._lastXYWH) {
      this._local.set('deserializedXYWH', deserializeXYWH(xywh));
      this._lastXYWH = xywh;
    }

    return (this._local.get('deserializedXYWH') as XYWH) ?? [0, 0, 0, 0];
  }

  /**
   * The bound of the element after rotation.
   * The bound without rotation should be created by `Bound.deserialize(this.xywh)`.
   */
  get elementBound() {
    if (this.rotate) {
      return Bound.from(getBoundWithRotation(this));
    }

    return Bound.deserialize(this._transientXYWH ?? this.xywh);
  }

  get transientXYWH(): SerializedXYWH | undefined {
    return this._transientXYWH;
  }

  set transientXYWH(xywh: SerializedXYWH | undefined) {
    const oldValue = this._transientXYWH ?? this.xywh;
    this._transientXYWH = xywh;
    this._lastXYWH = null;
    this._local.delete('deserializedXYWH');
    const nextValue = this._transientXYWH ?? this.xywh;
    if (oldValue === nextValue) {
      return;
    }
    this._onChange({
      props: {
        xywh: nextValue,
      },
      oldValues: {
        xywh: oldValue,
      },
      local: true,
    });
  }

  /** Presentation-only hidden span of the text. It never enters the Y.Map or serialized data. */
  get transientTextHidden(): TextSpan | undefined {
    return this._transientTextHidden;
  }

  set transientTextHidden(hidden: TextSpan | undefined) {
    const oldValue = this._transientTextHidden;
    if (oldValue?.from === hidden?.from && oldValue?.to === hidden?.to) return;
    this._transientTextHidden = hidden;
    this._onChange({
      props: { transientTextHidden: hidden },
      oldValues: { transientTextHidden: oldValue },
      local: true,
    });
  }

  get externalBound(): Bound | null {
    if (!this._local.has('externalBound')) {
      const bound = this.externalXYWH
        ? Bound.deserialize(this.externalXYWH)
        : null;

      this._local.set('externalBound', bound);
    }

    return this._local.get('externalBound') as Bound | null;
  }

  get group(): GfxGroupModel | null {
    if (!this._parentGroupId) return null;
    return this.surface.getGroupById(this._parentGroupId);
  }

  /**
   * Return the ancestor elements in order from the most recent to the earliest.
   */
  get groups(): GfxGroupModel[] {
    return this.surface.getGroups(this.id);
  }

  get h() {
    return this.deserializedXYWH[3];
  }

  get id() {
    return this._id;
  }

  get isConnected() {
    return this.surface.hasElementById(this.id);
  }

  get responseBound() {
    return this.elementBound.expand(this.responseExtension);
  }

  get persistedRotate(): number {
    const value =
      this.yMap.get('rotate') ??
      this._preserved.get('rotate') ??
      this._local.get('rotate') ??
      this._stashed.get('rotate') ??
      0;

    return typeof value === 'number' ? value : 0;
  }

  abstract get type(): string;

  get transientRotate(): number | undefined {
    return this._transientRotate;
  }

  set transientRotate(rotate: number | undefined) {
    const oldValue = this.rotate;
    this._transientRotate = rotate;
    const nextValue = this.rotate;
    if (oldValue === nextValue) {
      return;
    }
    this._onChange({
      props: {
        rotate: nextValue,
      },
      oldValues: {
        rotate: oldValue,
      },
      local: true,
    });
  }

  get w() {
    return this.deserializedXYWH[2];
  }

  get x() {
    return this.deserializedXYWH[0];
  }

  get y() {
    return this.deserializedXYWH[1];
  }

  constructor(options: {
    id: string;
    yMap: Y.Map<unknown>;
    model: SurfaceBlockModel;
    stashedStore: Map<unknown, unknown>;
    onChange: (payload: {
      props: Record<string, unknown>;
      oldValues: Record<string, unknown>;
      local: boolean;
    }) => void;
  }) {
    const { id, yMap, model, stashedStore, onChange } = options;

    this._id = id;
    this.yMap = yMap;
    this.surface = model;
    this._stashed = stashedStore as Map<keyof Props, unknown>;
    this._onChange = onChange;

    this.seed = randomSeed();
  }

  containsBound(bounds: Bound): boolean {
    return getPointsFromBoundWithRotation(this).some(point =>
      bounds.containsPoint(point)
    );
  }

  getLineIntersections(start: IVec, end: IVec) {
    const points = getPointsFromBoundWithRotation(this);
    return linePolygonIntersects(start, end, points);
  }

  getNearestPoint(point: IVec) {
    const points = getPointsFromBoundWithRotation(this);
    return polygonNearestPoint(points, point);
  }

  getRelativePointLocation(relativePoint: IVec) {
    const bound = Bound.deserialize(this.xywh);
    const point = bound.getRelativePoint(relativePoint);
    const rotatePoint = rotatePoints([point], bound.center, this.rotate)[0];
    const points = rotatePoints(bound.points, bound.center, this.rotate);
    const tangent = polygonGetPointTangent(points, rotatePoint);
    return new PointLocation(rotatePoint, tangent);
  }

  includesPoint(
    x: number,
    y: number,
    opt: PointTestOptions,
    __: EditorHost
  ): boolean {
    const bound = opt.useElementBound ? this.elementBound : this.responseBound;
    return bound.isPointInBound([x, y]);
  }

  intersectsBound(bound: Bound): boolean {
    return (
      this.containsBound(bound) ||
      bound.points.some((point, i, points) =>
        this.getLineIntersections(point, points[(i + 1) % points.length])
      )
    );
  }

  isLocked(): boolean {
    return isLockedImpl(this);
  }

  isLockedByAncestor(): boolean {
    return isLockedByAncestorImpl(this);
  }

  isLockedBySelf(): boolean {
    return isLockedBySelfImpl(this);
  }

  lock() {
    lockElementImpl(this.surface.store, this);
  }

  onCreated() {}

  onDestroyed() {
    this._disposable.dispose();
    this.propsUpdated.complete();
  }

  pop(prop: keyof Props | string) {
    if (!this._stashed.has(prop)) {
      return;
    }

    const value = this._stashed.get(prop);
    this._stashed.delete(prop);
    // @ts-expect-error ignore
    delete this[prop];

    if (getFieldPropsSet(this).has(prop as string)) {
      if (!isEqual(value, this.yMap.get(prop as string))) {
        this.yMap.set(prop as string, value);
      }
    } else {
      console.warn('pop a prop that is not field or local:', prop);
    }
  }

  /** Native Y.js JSON conversion, with a boundary before every shared value. */
  static *serializeValueWork(value: unknown): Generator<void, unknown> {
    type Frame = {
      iterator: Iterator<unknown>;
      result: Record<string, unknown> | unknown[];
      key?: string | number;
      parent?: Frame;
    };
    const frames: Frame[] = [];
    const enter = (
      value: unknown,
      parent?: Frame,
      key?: string | number
    ): unknown => {
      if (value instanceof Y.Map) {
        frames.push({ iterator: value.entries(), result: {}, parent, key });
        return undefined;
      }
      if (value instanceof Y.Array) {
        frames.push({
          iterator: value[Symbol.iterator](),
          result: [],
          parent,
          key,
        });
        return undefined;
      }
      return value instanceof Y.AbstractType ? value.toJSON() : value;
    };
    yield;
    const terminal = enter(value);
    if (!frames.length) return terminal;
    while (frames.length) {
      const frame = frames[frames.length - 1];
      yield;
      const next = frame.iterator.next();
      if (next.done) {
        frames.pop();
        if (!frame.parent) return frame.result;
        (frame.parent.result as Record<string | number, unknown>)[frame.key!] =
          frame.result;
        continue;
      }
      const key = Array.isArray(frame.result)
        ? frame.result.length
        : (next.value as [string, unknown])[0];
      const child = Array.isArray(frame.result)
        ? next.value
        : (next.value as [string, unknown])[1];
      const depth = frames.length;
      const result = enter(child, frame, key);
      if (frames.length === depth)
        (frame.result as Record<string | number, unknown>)[key] = result;
    }
    return undefined;
  }

  *serializeWork(): Generator<void, SerializedElement> {
    const result = (yield* GfxPrimitiveElementModel.serializeValueWork(
      this.yMap
    )) as SerializedElement;
    result.xywh = this.xywh;
    result.index = this.index;
    return result;
  }

  serialize() {
    const work = this.serializeWork();
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }

  stash(prop: keyof Props | string) {
    if (this._stashed.has(prop)) {
      return;
    }

    if (!getFieldPropsSet(this).has(prop as string)) {
      return;
    }

    const curVal = this[prop as unknown as keyof GfxPrimitiveElementModel];

    this._stashed.set(prop, curVal);

    Object.defineProperty(this, prop, {
      configurable: true,
      enumerable: true,
      get: () => this._stashed.get(prop),
      set: (original: unknown) => {
        const value = convertProps(prop as string, original, this);
        const oldValue = this._stashed.get(prop);
        const derivedProps = getDerivedProps(
          prop as string,
          original,
          this as unknown as GfxPrimitiveElementModel
        );

        this._stashed.set(prop, value);
        this._onChange({
          props: {
            [prop]: value,
          },
          oldValues: {
            [prop]: oldValue,
          },
          local: true,
        });

        updateDerivedProps(
          derivedProps,
          this as unknown as GfxPrimitiveElementModel
        );
      },
    });
  }

  unlock() {
    unlockElementImpl(this.surface.store, this);
  }

  @local()
  accessor display: boolean = true;

  /**
   * In some cases, you need to draw something related to the element, but it does not belong to the element itself.
   * And it is also interactive, you can select element by clicking on it. E.g. the title of the group element.
   * In this case, we need to store this kind of external xywh in order to do hit test. This property should not be synced to the doc.
   * This property should be updated every time it gets rendered.
   */
  @watch((_, instance) => {
    instance['_local'].delete('externalBound');
  })
  @local()
  accessor externalXYWH: SerializedXYWH | undefined = undefined;

  @field(false)
  accessor hidden: boolean = false;

  // Layer index until the element is placed; then its structure entry's o.
  private _unplacedIndex = 'a0';

  /** Layer index: the order of the element's structure entry. */
  get index(): string {
    return (
      this.surface.store.pageStructure.entry(this.id)?.o ?? this._unplacedIndex
    );
  }

  set index(value: string) {
    const structure = this.surface.store.pageStructure;
    if (structure.entry(this.id) === undefined) {
      this._unplacedIndex = value;
      return;
    }
    if (this.surface.store.readonly) return;
    this.surface.store.transact(() =>
      structure.writer.setOrder(this.id, value)
    );
  }

  @field()
  accessor lockedBySelf: boolean | undefined = false;

  @local()
  accessor opacity: number = 1;

  @local()
  accessor responseExtension: [number, number] = [0, 0];

  @field()
  accessor seed!: number;

  @field()
  accessor comments: Record<string, boolean> | undefined = undefined;
}

export abstract class GfxGroupLikeElementModel<
  Props extends BaseElementProps = BaseElementProps,
>
  extends GfxPrimitiveElementModel<Props>
  implements GfxGroupCompatibleInterface
{
  private _childIds: string[] = [];

  private readonly _mutex = createMutex();

  /**
   * Prop of a new element that names its members when they are the g of
   * their structure entries, as for groups; a mind map keeps its shapes in
   * its node map and leaves it unset.
   */
  static readonly structureMembersProp: string | undefined = undefined;

  [gfxGroupCompatibleSymbol] = true as const;

  readonly stacksChildren = true;

  get childElements() {
    return groupMembers(this.id, this.surface, this.childIds);
  }

  get loadedChildElements() {
    return loadedGroupMembers(this.surface, this.childIds);
  }

  /**
   * The ids of the children. Its role is to provide a unique way to access the children.
   * You should update this field through `setChildIds` when the children are added or removed.
   */
  get childIds() {
    return this._childIds;
  }

  get descendantElements(): GfxModel[] {
    return descendantElementsImpl(this);
  }

  get loadedDescendantElements(): GfxModel[] {
    return loadedDescendantElementsImpl(this);
  }

  get xywh() {
    this._mutex(() => {
      const curXYWH =
        (this._local.get('xywh') as SerializedXYWH) ?? '[0,0,0,0]';
      const newXYWH = this._getXYWH().serialize();

      if (curXYWH !== newXYWH || !this._local.has('xywh')) {
        this._local.set('xywh', newXYWH);

        if (curXYWH !== newXYWH) {
          this._onChange({
            props: {
              xywh: newXYWH,
            },
            oldValues: {
              xywh: curXYWH,
            },
            local: true,
          });
        }
      }
    });

    return (this._local.get('xywh') as SerializedXYWH) ?? '[0,0,0,0]';
  }

  set xywh(_) {}

  protected _getXYWH(): Bound {
    let bound: Bound | undefined;

    // The drawn bound: members still loading join it once they arrive.
    this.loadedChildElements.forEach(child => {
      if (child instanceof GfxPrimitiveElementModel && child.hidden) {
        return;
      }

      bound = bound ? bound.unite(child.elementBound) : child.elementBound;
    });

    if (bound) {
      this._local.set('xywh', bound.serialize());
    } else {
      this._local.delete('xywh');
    }

    return bound ?? new Bound(0, 0, 0, 0);
  }

  abstract addChild(element: GfxModel): void;

  /**
   * Takes the members from the page structure after it changed; for groups
   * whose members are the g of their structure entries.
   */
  syncMembers(_local: boolean): void {}

  hasChild(element: GfxCompatibleInterface) {
    return '_parentGroupId' in element && element._parentGroupId === this.id;
  }

  /**
   * Check if the group has the given descendant.
   */
  hasDescendant(element: GfxCompatibleInterface): boolean {
    return hasDescendantElementImpl(this, element);
  }

  /**
   * Remove the child from the group
   */
  abstract removeChild(element: GfxCompatibleInterface): void;

  /**
   * Set the new value of the childIds
   * @param value the new value of the childIds
   * @param fromLocal if true, the change is happened in the local
   */
  setChildIds(value: string[], fromLocal: boolean) {
    const oldChildIds = this.childIds;
    const oldSet = new Set(oldChildIds);
    const newSet = new Set(value);

    const findChild = (childId: string) => {
      const surfaceChild = this.surface.getElementById(childId);
      if (surfaceChild) return surfaceChild;
      const block = this.surface.store.getBlock(childId);
      if (block?.model && '_parentGroupId' in block.model) {
        return block.model as { _parentGroupId: string | null };
      }
      return null;
    };

    // Update _parentGroupId on removed children
    for (const childId of oldChildIds) {
      if (!newSet.has(childId)) {
        const child = findChild(childId);
        if (child) child._parentGroupId = null;
      }
    }

    // Update _parentGroupId on added children
    for (const childId of value) {
      if (!oldSet.has(childId)) {
        const child = findChild(childId);
        if (child) child._parentGroupId = this.id;
      }
    }

    this._childIds = value;

    this._onChange({
      props: {
        childIds: value,
      },
      oldValues: {
        childIds: oldChildIds,
      },
      local: fromLocal,
    });
  }
}

export function syncElementFromY(
  model: GfxPrimitiveElementModel,
  callback: (payload: {
    props: Record<string, unknown>;
    oldValues: Record<string, unknown>;
    local: boolean;
  }) => void
) {
  const disposables: Record<string, () => void> = {};
  const observer = (
    event: Y.YMapEvent<unknown>,
    transaction: Y.Transaction
  ) => {
    const props: Record<string, unknown> = {};
    const oldValues: Record<string, unknown> = {};

    event.keysChanged.forEach(key => {
      const type = event.changes.keys.get(key);
      const oldValue = event.changes.keys.get(key)?.oldValue;

      if (!type) {
        return;
      }

      if (type.action === 'update' || type.action === 'add') {
        const value = model.yMap.get(key);

        if (value instanceof Y.Text) {
          disposables[key]?.();
          disposables[key] = watchText(key, value, callback);
        }

        model['_preserved'].set(key, value);
        props[key] = value;
        oldValues[key] = oldValue;
      } else {
        model['_preserved'].delete(key);
        oldValues[key] = oldValue;
      }
    });

    callback({
      props,
      oldValues,
      local: transaction.local,
    });
  };

  Array.from(model.yMap.entries()).forEach(([key, value]) => {
    if (value instanceof Y.Text) {
      disposables[key] = watchText(key, value, callback);
    }

    model['_preserved'].set(key, value);
  });

  model.yMap.observe(observer);
  disposables['ymap'] = () => {
    model.yMap.unobserve(observer);
  };

  return () => {
    Object.values(disposables).forEach(fn => fn());
  };
}

function watchText(
  key: string,
  value: Y.Text,
  callback: (payload: {
    props: Record<string, unknown>;
    oldValues: Record<string, unknown>;
    local: boolean;
  }) => void
) {
  const fn = (_: Y.YTextEvent, transaction: Y.Transaction) => {
    callback({
      props: {
        [key]: value,
      },
      oldValues: {},
      local: transaction.local,
    });
  };

  value.observe(fn);

  return () => {
    value.unobserve(fn);
  };
}
